import { useEffect, useId, useRef } from "react";
import type { HTMLAttributes, MouseEvent, ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "../lib/cn";

export interface DialogProps extends Omit<HTMLAttributes<HTMLDialogElement>, "title"> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Título obrigatório: é o nome acessível do diálogo. */
  title: ReactNode;
  description?: ReactNode;
  /** Rodapé: cancelar à esquerda, ação (danger isolada) à direita. */
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Confirmação destrutiva não deve fechar por clique fora. */
  dismissOnBackdrop?: boolean;
  showCloseButton?: boolean;
  closeLabel?: string;
}

const SIZES: Record<NonNullable<DialogProps["size"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
};

/**
 * `<dialog>` NATIVO: o foco preso, o ESC e a devolução do foco ao gatilho
 * vem do navegador — sem Radix, sem biblioteca de foco.
 * Uso: confirmação/ação pontual. Nunca navegação (seção 6).
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  footer,
  size = "md",
  dismissOnBackdrop = true,
  showCloseButton = true,
  closeLabel = "Fechar",
  className,
  children,
  ...props
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-description`;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const handleClose = () => onOpenChange(false);
    node.addEventListener("close", handleClose);
    return () => node.removeEventListener("close", handleClose);
  }, [onOpenChange]);

  // o <dialog> modal já bloqueia interação, mas não trava o scroll do body
  // em todos os navegadores
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const onBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (!dismissOnBackdrop) return;
    if (event.target === ref.current) onOpenChange(false);
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClick={onBackdropClick}
      onCancel={(event) => {
        if (!dismissOnBackdrop) {
          // ESC continua fechando: travar o ESC quebra a expectativa do usuário.
          // O que `dismissOnBackdrop=false` protege é o clique acidental fora.
          event.currentTarget.close();
        }
      }}
      className={cn(
        "fixed inset-0 m-auto h-fit w-[calc(100%_-_2rem)] p-0",
        SIZES[size],
        // painel flutuante de raio 20, sem borda: a sombra de overlay é quem separa
        "rounded-xl bg-surface-raised text-foreground shadow-lg",
        "open:animate-dialog-in",
        className,
      )}
      {...props}
    >
      {open ? (
        <div className="flex max-h-[85dvh] flex-col">
          <header className="flex items-start justify-between gap-3 px-6 pt-6 pb-2">
            <div className="min-w-0 flex flex-col gap-1.5">
              <h2 id={titleId} className="text-lg">
                {title}
              </h2>
              {description ? (
                <p id={descriptionId} className="text-sm text-muted-foreground">
                  {description}
                </p>
              ) : null}
            </div>
            {showCloseButton ? (
              <button
                type="button"
                aria-label={closeLabel}
                onClick={() => onOpenChange(false)}
                className="hit-44 -mr-2 -mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            ) : null}
          </header>

          <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 text-sm scrollbar-thin", footer ? "py-3" : "pt-3 pb-6")}>
            {children}
          </div>

          {footer ? (
            <footer className="flex flex-col-reverse gap-2 px-6 pt-4 pb-6 sm:flex-row sm:items-center sm:justify-end">
              {footer}
            </footer>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
